import { SecondaryAction } from 'app/view/components/actions';
import useTenantContext from '@broadleaf/admin-components/dist/tracking/hooks/useTenantContext';
import { get, isEmpty } from 'lodash';
import { useMemo } from 'react';

/**
 * Add actions to view EFlyer Web or PDF on the commerce site.
 *
 * @param props Contains the form state with the EFlyer data.
 * @returns {JSX.Element|null}
 * @constructor
 */
export const EFlyerAction = props => {
  const tenantContext = useTenantContext();

  const uriParts = useMemo(() => {
    const tenantState = tenantContext[0];
    const applicationId = tenantState.applicationId || 'AURORA';
    const targetApp = tenantState.applicationById[applicationId];

    let dealerLocationParam = '';
    let host = location.host
      // remove admin prefix
      .replace('admin-', '')
      .replace('admin.', '')
      // Environment specific host name fixes
      .replace('8446', '8456')
      .replace('dev.', 'dev1.');

    const domainType = get(targetApp, 'domainType', null);
    if (domainType === 'DOMAIN_PREFIX' || domainType === 'DOMAIN') {
      host = resolveHostFromApp(targetApp, host);
    } else if (applicationId !== 'AURORA' && !isEmpty(targetApp)) {
      const dealerNetworkId = get(targetApp, 'dealerNetworkId');
      const dealerNetworkApp = tenantState.applicationById[dealerNetworkId];

      if (dealerNetworkApp) {
        host = resolveHostFromApp(dealerNetworkApp, host);
      } else {
        const nationalSiteApp = findNationalSiteApp(tenantState, applicationId);
        host = resolveHostFromApp(nationalSiteApp, host);
      }

      const identifierValue = get(targetApp, 'identifierValue');
      dealerLocationParam = `?dealerLocation=${identifierValue}`;
    }

    return {
      host,
      contentUri: props.state.data.uri,
      dealerLocationParam
    };
  }, [tenantContext, props.state.data.uri]);

  const modelType = get(props, 'state.data.model.modelType', '');
  if (modelType !== 'EFLYER') {
    return null;
  }

  const handleOpenEflyerPdf = () => {
    const url = buildEflyerUrl(uriParts, true);
    window.open(url, '_blank');
  };

  const handleOpenEFlyerWeb = () => {
    const url = buildEflyerUrl(uriParts, false);
    window.open(url, '_blank');
  };

  return (
    <>
      <SecondaryAction onClick={handleOpenEFlyerWeb}>
        View EFlyer Web
      </SecondaryAction>
      <SecondaryAction onClick={handleOpenEflyerPdf} className={'border-0'}>
        View EFlyer PDF
      </SecondaryAction>
    </>
  );
};

function buildEflyerUrl(parts, isPdf) {
  let pdf = '';
  if (isPdf) {
    pdf = '/pdf';
  }
  const contentUri = parts.contentUri.replace('/eflyer', '');
  return `https://${parts.host}/eflyer${pdf}${contentUri}${parts.dealerLocationParam}`;
}

function resolveHostFromApp(app, currentHost) {
  if (!app) return currentHost;
  let newHost = currentHost;
  const domainType = get(app, 'domainType', null);
  if (domainType === 'DOMAIN_PREFIX') {
    const domainPrefix = get(app, 'domainPrefix', null);
    newHost = `${domainPrefix}.${newHost}`;
  } else if (domainType === 'DOMAIN') {
    let fullDomain = get(app, 'fullDomain', null);
    if (fullDomain !== null) {
      if (newHost.includes(':8456')) {
        fullDomain += ':8456';
      }
      newHost = fullDomain;
    }
  }
  return newHost;
}

function findNationalSiteApp(tenantState, applicationId) {
  return Object.values(tenantState.applicationById || {}).find(
    app =>
      get(app, 'nationalSite') &&
      get(app, 'natlSiteMembers', []).includes(applicationId)
  );
}
